import { motion } from 'framer-motion';

const pillars = [
  {
    title: 'Strategic Intelligence',
    desc: 'Long-horizon advisory systems designed to strengthen organisational clarity, scalability, resilience, and executive decision architecture.',
  },
  {
    title: 'AI Infrastructure & Strategic Intelligence',
    desc: 'AI readiness diagnostics, infrastructure pathways, and enterprise intelligence systems designed for the next era of machine intelligence.',
  },
  {
    title: 'Venture Architecture',
    desc: 'Origination and structuring of next-generation ventures, platforms, and scalable future-facing ecosystems.',
  },
  {
    title: 'Future Systems Design',
    desc: 'Research and development across advanced technologies, infrastructure, governance, and civilisation-scale innovation frameworks.',
  },
];

const HeroSection = () => {
  const navLinks = [
    { name: 'ABOUT', href: '#about' },
    { name: 'PROJECTS', href: '#projects' },
    { name: 'AI INFRASTRUCTURE', href: '#ai-infrastructure' },
    { name: 'STRATEGIC ADVISORY', href: '#consultancy' },
  ];

  const navLinksRow2 = [
    { name: 'EVOLUTION', href: '#evolution' },
    { name: 'CONTRIBUTION', href: '#contribution' },
    { name: 'INVESTOR INVITATION', href: '#investor' },
    { name: 'OPEN INTELLIGENCE', href: '#intelligence' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
      style={{
        backgroundImage: 'url(/hero-bg.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-8 lg:px-16 flex items-center">
        {/* Left side - Text content */}
        <div className="w-full lg:w-3/5 text-center lg:text-left">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-2"
          >
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-light tracking-[0.4em] text-white mb-1"
              style={{ fontFamily: "'Cinzel', 'Times New Roman', serif" }}
              data-testid="hero-title"
            >
              CELESTIAL
            </h1>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-light tracking-[0.4em] text-white"
              style={{ fontFamily: "'Cinzel', 'Times New Roman', serif" }}
            >
              MINDS
            </h1>
          </motion.div>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-sm md:text-base tracking-[0.3em] text-white/60 mb-10"
          >
            EXPAND YOUR MIND
          </motion.p>

          {/* Navigation Buttons - Row 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-wrap justify-center lg:justify-start gap-3 mb-3"
          >
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="px-5 py-2.5 border border-[#c9a227]/40 text-[#c9a227] text-xs tracking-[0.15em] hover:bg-[#c9a227]/10 hover:border-[#c9a227]/70 transition-all duration-300"
                data-testid={`nav-btn-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </button>
            ))}
          </motion.div>

          {/* Navigation Buttons - Row 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-wrap justify-center lg:justify-start gap-3 mb-12"
          >
            {navLinksRow2.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="px-5 py-2.5 border border-[#c9a227]/40 text-[#c9a227] text-xs tracking-[0.15em] hover:bg-[#c9a227]/10 hover:border-[#c9a227]/70 transition-all duration-300"
                data-testid={`nav-btn-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </button>
            ))}
          </motion.div>

          {/* Infinite Possibilities */}
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="text-2xl md:text-3xl lg:text-4xl tracking-[0.25em] mb-4"
            style={{ 
              fontFamily: "'Cinzel', 'Times New Roman', serif",
              color: '#c9a227'
            }}
          >
            INFINITE POSSIBILITIES
          </motion.h2>

          {/* Quote */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="text-lg md:text-xl italic mb-10"
            style={{ 
              color: '#c9a227',
              fontFamily: "'Cormorant Garamond', Georgia, serif"
            }}
          >
            We do not follow the stars. We create them.
          </motion.p>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="text-sm md:text-base text-white/70 leading-relaxed max-w-2xl mx-auto lg:mx-0 space-y-4"
          >
            <p>
              Celestial Minds&#8482; is an independent strategic intelligence and AI infrastructure firm helping organisations evolve into resilient, AI-native systems prepared for the next era of markets, machine intelligence, and global transformation.
            </p>
            <p>
              Celestial Minds operates through several strategic intelligence and enterprise evolution frameworks, including the{' '}
              <a
                href="https://livinghighlevelintelligencesystem.com"
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline"
                style={{ color: '#c9a227' }}
                data-testid="hero-lhlis-link"
              >
                Living High-Level Intelligence System&#8482;
              </a>
              {' '}&mdash; a structured advisory and decision-intelligence architecture designed to help organisations modernise intelligently in the era of AI and long-horizon economic transformation.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, #050505 0%, transparent 100%)'
        }}
      />
    </section>
  );
};

/* Four Pillars — exported separately for placement after hero */
export const FourPillars = () => {
  return (
    <section className="relative py-16 lg:py-20" data-testid="four-pillars">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: 'rgba(201,162,39,0.5)' }}>
            Core Capabilities
          </p>
          <h3
            className="text-xl md:text-2xl font-light tracking-[0.1em] text-white"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            What We Do
          </h3>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="card-glow p-6"
              style={{
                background: 'rgba(10,22,40,0.4)',
                border: '1px solid rgba(201,162,39,0.12)',
                borderRadius: '4px',
              }}
              data-testid={`pillar-${i + 1}`}
            >
              <h4
                className="text-xs uppercase tracking-[0.15em] mb-3"
                style={{ color: '#c9a227', fontFamily: "'Cinzel', serif" }}
              >
                {p.title}
              </h4>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {p.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* LHLIS — Enterprise Operating Layer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 card-glow p-8 md:p-10 text-center"
          style={{
            background: 'rgba(10,22,40,0.5)',
            border: '1px solid rgba(201,162,39,0.18)',
            borderRadius: '4px',
          }}
          data-testid="lhlis-block"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: 'rgba(201,162,39,0.5)' }}>
            Enterprise Intelligence Framework
          </p>
          <h3
            className="text-lg md:text-xl font-light tracking-[0.1em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Living High-Level Intelligence System&#8482;
          </h3>
          <p className="text-sm leading-relaxed max-w-2xl mx-auto mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
            The enterprise intelligence and organisational transformation framework powering strategic diagnostics,
            AI readiness, operational evolution, and long-horizon decision architecture within the Celestial Minds ecosystem.
          </p>
          <a
            href="https://livinghighlevelintelligencesystem.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2 text-xs uppercase tracking-widest"
            data-testid="lhlis-cta-link"
          >
            Explore the Framework
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
